import { useState, useEffect, useRef } from "react";
import { Container, FormContainer, TextareaStyled, ButtonGroup, ButtonStyled, SelectContainer, Label, SelectWrapper, SelectTrigger, SelectValue, SelectValueName, SelectValueBadge, SelectDropdown, SelectItem, SelectItemRow, SelectItemName, SelectItemBadge, SelectItemEmail, SelectOverlay } from "./styles";
import { ChevronDown, Check } from "lucide-react";
import { comum } from "../../../../styles/Themes/light";
import comentarioService, { ICreateComentario } from "../../../../services/comentarioService";
import toast from "react-hot-toast";
import { toastDefsError, toastDefs } from "../../../../config/toastConfig";
import { useTheme } from "styled-components";
import { BadgeTipoDestinatario } from "../CardComentario/styles";
import { getTipoLabelDestinatario } from "../../../../utility/comentarioTipos";

interface FormNovoComentarioProps {
    id_proposta: number;
    onComentarioCriado: () => void;
    user: {
        tipo: string;
    };
    proposta?: {
        id_usuario?: number;
        id_escritorio_solicitante?: number;
        nome_profissional?: string;
        email?: string;
        nome_escritorio?: string;
        email_escritorio?: string;
    };
    onOpenChange?: (isOpen: boolean) => void;
    forceOpen?: boolean;
}

interface DestinatarioOption {
    id: number | null;
    nome: string;
    tipo: 'Correspondente' | 'Solicitante' | 'Operador';
    email: string;
    tipo_destinatario: 'Profissional' | 'Solicitante' | 'OperadoresInterno';
}

export default function FormNovoComentario({ 
    id_proposta, 
    onComentarioCriado, 
    user,
    proposta,
    onOpenChange,
    forceOpen = false
}: FormNovoComentarioProps) {
    const theme = useTheme() as any;
    const [isOpen, setIsOpen] = useState(forceOpen);
    const [texto, setTexto] = useState("");
    const [destinatario, setDestinatario] = useState<DestinatarioOption | null>(null);
    const [destinatariosOptions, setDestinatariosOptions] = useState<DestinatarioOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingDestinatarios, setIsLoadingDestinatarios] = useState(false);
    const [erroTexto, setErroTexto] = useState("");
    const [erroDestinatario, setErroDestinatario] = useState("");
    const [selectOpen, setSelectOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isOperador = user.tipo === "Operador" || user.tipo === "Admin";
    
    const podeCriarComentario = (): boolean => {
        if (isOperador) {
            return true;
        }
        
        if (proposta) {
            if (user.tipo === "Solicitante" && proposta.id_escritorio_solicitante) {
                return true;
            }
            if ((user.tipo === "Profissional" || user.tipo === "Correspondente") && proposta.id_usuario) {
                return true;
            }
        }
        
        return false;
    };

    useEffect(() => {
        const carregarDestinatarios = async () => {
            if (isOperador && id_proposta) {
                setIsLoadingDestinatarios(true);
                try {
                    const destinatarios = await comentarioService.getDestinatarios(id_proposta);
                    const options: DestinatarioOption[] = destinatarios.map((dest: any) => ({
                        id: dest.id,
                        nome: dest.nome,
                        tipo: dest.tipo === 'Correspondente' ? 'Correspondente' : dest.tipo === 'Operador' ? 'Operador' : 'Solicitante',
                        email: dest.email || '',
                        tipo_destinatario: dest.tipo_destinatario
                    }));
                    setDestinatariosOptions(options);
                    if (options.length > 0 && !destinatario) {
                        setDestinatario(options[0]);
                    }
                } catch (error: any) {
                    console.error('Erro ao carregar destinatários:', error);
                } finally {
                    setIsLoadingDestinatarios(false);
                }
            }
        };

        if (isOpen && isOperador) {
            carregarDestinatarios();
        }
    }, [isOpen, id_proposta, isOperador]);

    useEffect(() => {
        if (isOpen && textareaRef.current) {
            textareaRef.current.focus();
            autoResize(textareaRef.current);
        }
    }, [isOpen]);


    const autoResize = (textarea: HTMLTextAreaElement) => {
        textarea.style.height = 'auto';
        const maxHeight = 300;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const novoTexto = e.target.value;
        setTexto(novoTexto);
        autoResize(e.target);
        
        if (erroTexto && novoTexto.trim()) {
            setErroTexto("");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && e.ctrlKey && texto.trim()) {
            e.preventDefault();
            handleSalvar();
        }
    };

    const handleSalvar = async () => {
        if (isLoading) return;
        
        let temErro = false;
        
        if (!texto.trim()) {
            setErroTexto("O comentário não pode estar vazio");
            temErro = true;
        } else {
            setErroTexto("");
        }
        
        if (isOperador && !destinatario) {
            setErroDestinatario("Selecione um destinatário");
            temErro = true;
        } else {
            setErroDestinatario("");
        }
        
        if (temErro) {
            return;
        }

        setIsLoading(true);
        try {
            const data: ICreateComentario = {
                id_proposta,
                texto: texto.trim(),
            };

            if (isOperador && destinatario) {
                if (destinatario.tipo_destinatario === 'OperadoresInterno') {
                    data.tipo_destinatario = 'OperadoresInterno';
                } else {
                    data.id_destinatario = destinatario.id;
                    data.tipo_destinatario = destinatario.tipo_destinatario;
                }
            }

            await comentarioService.createComentario(data);
            
            toast.success("Comentário criado com sucesso!", toastDefs);
            setTexto("");
            setIsOpen(false);
            setDestinatario(destinatariosOptions.length > 0 && isOperador ? destinatariosOptions[0] : null);
            onComentarioCriado();
            if (forceOpen) {
                onOpenChange?.(false);
            }
        } catch (error: any) {
            console.error('Erro ao criar comentário:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao criar comentário';
            toast.error(errorMessage, toastDefsError);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelar = () => {
        setTexto("");
        setIsOpen(false);
        setDestinatario(destinatariosOptions.length > 0 && isOperador ? destinatariosOptions[0] : null);
        if (forceOpen) {
            onOpenChange?.(false);
        }
    };

    useEffect(() => {
        if (forceOpen) {
            setIsOpen(true);
        }
    }, [forceOpen]);

    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    if (!podeCriarComentario()) {
        return null;
    }

    if (!isOpen && !forceOpen) {
        return null;
    }

    return (
        <Container>
            <FormContainer>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 500, color: 'var(--c6b7280)', margin: 0 }}>
                        Adicionar Comentário
                    </h3>
                </div>
                {(isOperador || user.tipo === 'Solicitante' || user.tipo === 'Profissional' || user.tipo === 'Correspondente') && (
                    <SelectContainer>
                        <Label htmlFor="select-destinatario">Para:</Label>
                        {!isOperador && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <BadgeTipoDestinatario>
                                    {getTipoLabelDestinatario('Operador')}
                                </BadgeTipoDestinatario>
                            </div>
                        )}
                        {isOperador && (
                            <>
                                {isLoadingDestinatarios ? (
                                    <div style={{ padding: '8px', color: 'var(--c6b7280)', fontSize: '14px' }}>Carregando destinatários...</div>
                                ) : destinatariosOptions.length > 0 ? (
                                    <SelectWrapper>
                                <SelectTrigger 
                                    type="button"
                                    id="select-destinatario"
                                    isOpen={selectOpen}
                                    onClick={() => setSelectOpen(!selectOpen)}
                                    aria-label="Selecionar destinatário"
                                    aria-expanded={selectOpen}
                                    aria-invalid={!!erroDestinatario}
                                    aria-describedby={erroDestinatario ? "erro-destinatario" : undefined}
                                >
                                    <SelectValue>
                                        {destinatario ? (
                                            <>
                                                <SelectValueName>
                                                    {destinatario.nome}
                                                </SelectValueName>
                                                <SelectValueBadge variant={destinatario.tipo === 'Correspondente' ? 'correspondente' : destinatario.tipo === 'Operador' ? 'correspondente' : 'solicitante'}>
                                                    {destinatario.tipo === 'Correspondente' ? 'Profissional' : destinatario.tipo === 'Operador' ? 'Operador' : 'Solicitante'}
                                                </SelectValueBadge>
                                            </>
                                        ) : (
                                            <SelectValueName style={{ color: comum.colors.interfaceText300 }}>
                                                Selecione...
                                            </SelectValueName>
                                        )}
                                    </SelectValue>
                                    <ChevronDown size={16} color={comum.colors.interfaceText300} />
                                </SelectTrigger>
                                <SelectDropdown isOpen={selectOpen} role="listbox">
                                    {destinatariosOptions.map((option) => (
                                        <SelectItem
                                            key={option.id}
                                            type="button"
                                            role="option"
                                            onClick={() => {
                                                setDestinatario(option);
                                                setSelectOpen(false);
                                                if (erroDestinatario) {
                                                    setErroDestinatario("");
                                                }
                                            }}
                                        >
                                            <SelectItemRow>
                                                {destinatario && destinatario.id === option.id && (
                                                    <Check size={16} color={comum.colors.primaryRed400} style={{ flexShrink: 0 }} />
                                                )}
                                                <SelectItemName>
                                                    {option.nome}
                                                </SelectItemName>
                                                <SelectItemBadge variant={option.tipo === 'Correspondente' ? 'correspondente' : option.tipo === 'Operador' ? 'correspondente' : 'solicitante'}>
                                                    {option.tipo === 'Correspondente' ? 'Profissional' : option.tipo === 'Operador' ? 'Operador' : 'Solicitante'}
                                                </SelectItemBadge>
                                            </SelectItemRow>
                                            {option.email && (
                                                <SelectItemEmail>
                                                    {option.email}
                                                </SelectItemEmail>
                                            )}
                                        </SelectItem>
                                    ))}
                                </SelectDropdown>
                            </SelectWrapper>
                                ) : (
                                    <div style={{ padding: '8px', color: 'var(--cef4444)', fontSize: '14px' }}>Nenhum destinatário disponível</div>
                                )}
                            </>
                        )}
                        {erroDestinatario && (
                            <div id="erro-destinatario" style={{ padding: '4px 8px', color: 'var(--cef4444)', fontSize: '12px', marginTop: '4px' }} role="alert">
                                {erroDestinatario}
                            </div>
                        )}
                        <SelectOverlay isOpen={selectOpen} onClick={() => setSelectOpen(false)} />
                    </SelectContainer>
                )}

                <div style={{ position: 'relative', width: '100%' }}>
                    <TextareaStyled
                        ref={textareaRef}
                        placeholder="Escreva seu comentário..."
                        value={texto}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        rows={4}
                        aria-label="Campo de texto do comentário"
                        aria-invalid={!!erroTexto}
                        aria-describedby={erroTexto ? "erro-texto" : undefined}
                        style={{
                            borderColor: erroTexto ? 'var(--cef4444)' : undefined
                        }}
                    />
                    {erroTexto && (
                        <div id="erro-texto" style={{ padding: '4px 8px', color: 'var(--cef4444)', fontSize: '12px' }} role="alert">
                            {erroTexto}
                        </div>
                    )}
                </div>

                <ButtonGroup>
                <ButtonStyled 
                    variant="outline" 
                    onClick={handleCancelar} 
                    disabled={isLoading}
                    aria-label="Cancelar criação de comentário"
                >
                    Cancelar
                </ButtonStyled>
                <ButtonStyled 
                    onClick={handleSalvar} 
                    disabled={!texto.trim() || isLoading || (isOperador && !destinatario)}
                    aria-label="Salvar comentário"
                >
                    {isLoading ? 'Salvando...' : 'Salvar'}
                </ButtonStyled>
                </ButtonGroup>
            </FormContainer>
        </Container>
    );
}

